/**
 * Currency metadata (symbols, names, decimal digits) and the symbol lookup.
 *
 * Lives in `@splitfiesta/shared` so this package depends on nothing: the
 * formatter here needs the symbols, and reaching into `@splitfiesta/i18n`
 * for them made the two packages import each other — fatal, because i18n's
 * web entry builds its singleton while that module is still initialising.
 * `@splitfiesta/i18n` re-exports all of it, so its public API is unchanged.
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  symbolNative: string;
  decimalDigits: number;
  rounding: number;
  namePlural: string;
}

export const CURRENCIES: Record<string, CurrencyInfo> = {
  AED: {
    code: 'AED',
    symbol: 'د.إ',
    name: 'United Arab Emirates Dirham',
    symbolNative: 'د.إ',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'UAE dirhams',
  },
  AFN: {
    code: 'AFN',
    symbol: '؋',
    name: 'Afghan Afghani',
    symbolNative: '؋',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Afghan Afghanis',
  },
  ALL: {
    code: 'ALL',
    symbol: 'L',
    name: 'Albanian Lek',
    symbolNative: 'L',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Albanian lekë',
  },
  AMD: {
    code: 'AMD',
    symbol: '֏',
    name: 'Armenian Dram',
    symbolNative: '֏',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Armenian drams',
  },
  ANG: {
    code: 'ANG',
    symbol: 'ƒ',
    name: 'Netherlands Antillean Guilder',
    symbolNative: 'ƒ',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Netherlands Antillean guilders',
  },
  AOA: {
    code: 'AOA',
    symbol: 'Kz',
    name: 'Angolan Kwanza',
    symbolNative: 'Kz',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Angolan kwanzas',
  },
  ARS: {
    code: 'ARS',
    symbol: '$',
    name: 'Argentine Peso',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Argentine pesos',
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Australian dollars',
  },
  AWG: {
    code: 'AWG',
    symbol: 'ƒ',
    name: 'Aruban Florin',
    symbolNative: 'ƒ',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Aruban florin',
  },
  AZN: {
    code: 'AZN',
    symbol: '₼',
    name: 'Azerbaijani Manat',
    symbolNative: '₼',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Azerbaijani manats',
  },
  BAM: {
    code: 'BAM',
    symbol: 'KM',
    name: 'Bosnia-Herzegovina Convertible Mark',
    symbolNative: 'KM',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Bosnia-Herzegovina convertible marks',
  },
  BBD: {
    code: 'BBD',
    symbol: 'Bds$',
    name: 'Barbadian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Barbadian dollars',
  },
  BDT: {
    code: 'BDT',
    symbol: '৳',
    name: 'Bangladeshi Taka',
    symbolNative: '৳',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Bangladeshi takas',
  },
  BGN: {
    code: 'BGN',
    symbol: 'лв',
    name: 'Bulgarian Lev',
    symbolNative: 'лв',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Bulgarian leva',
  },
  BHD: {
    code: 'BHD',
    symbol: 'BD',
    name: 'Bahraini Dinar',
    symbolNative: 'د.ب',
    decimalDigits: 3,
    rounding: 0.001,
    namePlural: 'Bahraini dinars',
  },
  BIF: {
    code: 'BIF',
    symbol: 'FBu',
    name: 'Burundian Franc',
    symbolNative: 'FBu',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Burundian francs',
  },
  BMD: {
    code: 'BMD',
    symbol: '$',
    name: 'Bermudian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Bermudian dollars',
  },
  BND: {
    code: 'BND',
    symbol: 'B$',
    name: 'Brunei Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Brunei dollars',
  },
  BOB: {
    code: 'BOB',
    symbol: 'Bs',
    name: 'Bolivian Boliviano',
    symbolNative: 'Bs',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Bolivian bolivianos',
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    name: 'Brazilian Real',
    symbolNative: 'R$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Brazilian reals',
  },
  BSD: {
    code: 'BSD',
    symbol: 'B$',
    name: 'Bahamian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Bahamian dollars',
  },
  BTN: {
    code: 'BTN',
    symbol: 'Nu.',
    name: 'Bhutanese Ngultrum',
    symbolNative: 'Nu.',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Bhutanese ngultrums',
  },
  BWP: {
    code: 'BWP',
    symbol: 'P',
    name: 'Botswanan Pula',
    symbolNative: 'P',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Botswanan pulas',
  },
  BYN: {
    code: 'BYN',
    symbol: 'Br',
    name: 'Belarusian Ruble',
    symbolNative: 'Br',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Belarusian rubles',
  },
  BZD: {
    code: 'BZD',
    symbol: 'BZ$',
    name: 'Belize Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Belize dollars',
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'Canadian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Canadian dollars',
  },
  CDF: {
    code: 'CDF',
    symbol: 'FC',
    name: 'Congolese Franc',
    symbolNative: 'FC',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Congolese francs',
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    name: 'Swiss Franc',
    symbolNative: 'CHF',
    decimalDigits: 2,
    rounding: 0.05,
    namePlural: 'Swiss francs',
  },
  CLP: {
    code: 'CLP',
    symbol: '$',
    name: 'Chilean Peso',
    symbolNative: '$',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Chilean pesos',
  },
  CNY: {
    code: 'CNY',
    symbol: '¥',
    name: 'Chinese Yuan',
    symbolNative: '¥',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Chinese yuan',
  },
  COP: {
    code: 'COP',
    symbol: '$',
    name: 'Colombian Peso',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Colombian pesos',
  },
  CRC: {
    code: 'CRC',
    symbol: '₡',
    name: 'Costa Rican Colón',
    symbolNative: '₡',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Costa Rican colóns',
  },
  CUP: {
    code: 'CUP',
    symbol: '$',
    name: 'Cuban Peso',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Cuban pesos',
  },
  CVE: {
    code: 'CVE',
    symbol: '$',
    name: 'Cape Verdean Escudo',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Cape Verdean escudos',
  },
  CZK: {
    code: 'CZK',
    symbol: 'Kč',
    name: 'Czech Koruna',
    symbolNative: 'Kč',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Czech korunas',
  },
  DJF: {
    code: 'DJF',
    symbol: 'Fdj',
    name: 'Djiboutian Franc',
    symbolNative: 'Fdj',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Djiboutian francs',
  },
  DKK: {
    code: 'DKK',
    symbol: 'kr',
    name: 'Danish Krone',
    symbolNative: 'kr',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Danish kroner',
  },
  DOP: {
    code: 'DOP',
    symbol: 'RD$',
    name: 'Dominican Peso',
    symbolNative: 'RD$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Dominican pesos',
  },
  DZD: {
    code: 'DZD',
    symbol: 'DA',
    name: 'Algerian Dinar',
    symbolNative: 'د.ج',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Algerian dinars',
  },
  EGP: {
    code: 'EGP',
    symbol: 'E£',
    name: 'Egyptian Pound',
    symbolNative: 'ج.م',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Egyptian pounds',
  },
  ERN: {
    code: 'ERN',
    symbol: 'Nfk',
    name: 'Eritrean Nakfa',
    symbolNative: 'Nfk',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Eritrean nakfas',
  },
  ETB: {
    code: 'ETB',
    symbol: 'Br',
    name: 'Ethiopian Birr',
    symbolNative: 'Br',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Ethiopian birrs',
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'Euro',
    symbolNative: '€',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'euros',
  },
  FJD: {
    code: 'FJD',
    symbol: 'FJ$',
    name: 'Fijian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Fijian dollars',
  },
  FKP: {
    code: 'FKP',
    symbol: '£',
    name: 'Falkland Islands Pound',
    symbolNative: '£',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Falkland Islands pounds',
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    symbolNative: '£',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'British pounds',
  },
  GEL: {
    code: 'GEL',
    symbol: '₾',
    name: 'Georgian Lari',
    symbolNative: '₾',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Georgian laris',
  },
  GHS: {
    code: 'GHS',
    symbol: 'GH₵',
    name: 'Ghanaian Cedi',
    symbolNative: 'GH₵',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Ghanaian cedis',
  },
  GIP: {
    code: 'GIP',
    symbol: '£',
    name: 'Gibraltar Pound',
    symbolNative: '£',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Gibraltar pounds',
  },
  GMD: {
    code: 'GMD',
    symbol: 'D',
    name: 'Gambian Dalasi',
    symbolNative: 'D',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Gambian dalasis',
  },
  GNF: {
    code: 'GNF',
    symbol: 'FG',
    name: 'Guinean Franc',
    symbolNative: 'FG',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Guinean francs',
  },
  GTQ: {
    code: 'GTQ',
    symbol: 'Q',
    name: 'Guatemalan Quetzal',
    symbolNative: 'Q',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Guatemalan quetzals',
  },
  GYD: {
    code: 'GYD',
    symbol: 'G$',
    name: 'Guyanese Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Guyanese dollars',
  },
  HKD: {
    code: 'HKD',
    symbol: 'HK$',
    name: 'Hong Kong Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Hong Kong dollars',
  },
  HNL: {
    code: 'HNL',
    symbol: 'L',
    name: 'Honduran Lempira',
    symbolNative: 'L',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Honduran lempiras',
  },
  HRK: {
    code: 'HRK',
    symbol: 'kn',
    name: 'Croatian Kuna',
    symbolNative: 'kn',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Croatian kunas',
  },
  HTG: {
    code: 'HTG',
    symbol: 'G',
    name: 'Haitian Gourde',
    symbolNative: 'G',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Haitian gourdes',
  },
  HUF: {
    code: 'HUF',
    symbol: 'Ft',
    name: 'Hungarian Forint',
    symbolNative: 'Ft',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Hungarian forints',
  },
  IDR: {
    code: 'IDR',
    symbol: 'Rp',
    name: 'Indonesian Rupiah',
    symbolNative: 'Rp',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Indonesian rupiahs',
  },
  ILS: {
    code: 'ILS',
    symbol: '₪',
    name: 'Israeli New Shekel',
    symbolNative: '₪',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Israeli new shekels',
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    symbolNative: '₹',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Indian rupees',
  },
  IQD: {
    code: 'IQD',
    symbol: 'ID',
    name: 'Iraqi Dinar',
    symbolNative: 'د.ع',
    decimalDigits: 3,
    rounding: 0.001,
    namePlural: 'Iraqi dinars',
  },
  IRR: {
    code: 'IRR',
    symbol: '﷼',
    name: 'Iranian Rial',
    symbolNative: '﷼',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Iranian rials',
  },
  ISK: {
    code: 'ISK',
    symbol: 'kr',
    name: 'Icelandic Króna',
    symbolNative: 'kr',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Icelandic krónur',
  },
  JMD: {
    code: 'JMD',
    symbol: 'J$',
    name: 'Jamaican Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Jamaican dollars',
  },
  JOD: {
    code: 'JOD',
    symbol: 'JD',
    name: 'Jordanian Dinar',
    symbolNative: 'د.أ',
    decimalDigits: 3,
    rounding: 0.001,
    namePlural: 'Jordanian dinars',
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'Japanese Yen',
    symbolNative: '￥',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Japanese yen',
  },
  KES: {
    code: 'KES',
    symbol: 'KSh',
    name: 'Kenyan Shilling',
    symbolNative: 'KSh',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Kenyan shillings',
  },
  KGS: {
    code: 'KGS',
    symbol: 'сом',
    name: 'Kyrgyzstani Som',
    symbolNative: 'сом',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Kyrgyzstani soms',
  },
  KHR: {
    code: 'KHR',
    symbol: '៛',
    name: 'Cambodian Riel',
    symbolNative: '៛',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Cambodian riels',
  },
  KMF: {
    code: 'KMF',
    symbol: 'CF',
    name: 'Comorian Franc',
    symbolNative: 'FC',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Comorian francs',
  },
  KPW: {
    code: 'KPW',
    symbol: '₩',
    name: 'North Korean Won',
    symbolNative: '₩',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'North Korean won',
  },
  KRW: {
    code: 'KRW',
    symbol: '₩',
    name: 'South Korean Won',
    symbolNative: '₩',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'South Korean won',
  },
  KWD: {
    code: 'KWD',
    symbol: 'KD',
    name: 'Kuwaiti Dinar',
    symbolNative: 'د.ك',
    decimalDigits: 3,
    rounding: 0.001,
    namePlural: 'Kuwaiti dinars',
  },
  KYD: {
    code: 'KYD',
    symbol: 'CI$',
    name: 'Cayman Islands Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Cayman Islands dollars',
  },
  KZT: {
    code: 'KZT',
    symbol: '₸',
    name: 'Kazakhstani Tenge',
    symbolNative: '₸',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Kazakhstani tenges',
  },
  LAK: {
    code: 'LAK',
    symbol: '₭',
    name: 'Lao Kip',
    symbolNative: '₭',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Lao kips',
  },
  LBP: {
    code: 'LBP',
    symbol: 'L£',
    name: 'Lebanese Pound',
    symbolNative: 'ل.ل',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Lebanese pounds',
  },
  LKR: {
    code: 'LKR',
    symbol: 'Rs',
    name: 'Sri Lankan Rupee',
    symbolNative: 'රු',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Sri Lankan rupees',
  },
  LRD: {
    code: 'LRD',
    symbol: 'L$',
    name: 'Liberian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Liberian dollars',
  },
  LSL: {
    code: 'LSL',
    symbol: 'L',
    name: 'Lesotho Loti',
    symbolNative: 'L',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Lesotho maloti',
  },
  LYD: {
    code: 'LYD',
    symbol: 'LD',
    name: 'Libyan Dinar',
    symbolNative: 'د.ل',
    decimalDigits: 3,
    rounding: 0.001,
    namePlural: 'Libyan dinars',
  },
  MAD: {
    code: 'MAD',
    symbol: 'MAD',
    name: 'Moroccan Dirham',
    symbolNative: 'د.م.',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Moroccan dirhams',
  },
  MDL: {
    code: 'MDL',
    symbol: 'lei',
    name: 'Moldovan Leu',
    symbolNative: 'lei',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Moldovan lei',
  },
  MGA: {
    code: 'MGA',
    symbol: 'Ar',
    name: 'Malagasy Ariary',
    symbolNative: 'Ar',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Malagasy ariaries',
  },
  MKD: {
    code: 'MKD',
    symbol: 'ден',
    name: 'Macedonian Denar',
    symbolNative: 'ден',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Macedonian denari',
  },
  MMK: {
    code: 'MMK',
    symbol: 'K',
    name: 'Myanmar Kyat',
    symbolNative: 'K',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Myanmar kyats',
  },
  MNT: {
    code: 'MNT',
    symbol: '₮',
    name: 'Mongolian Tugrik',
    symbolNative: '₮',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Mongolian tugriks',
  },
  MOP: {
    code: 'MOP',
    symbol: 'MOP$',
    name: 'Macanese Pataca',
    symbolNative: 'MOP$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Macanese patacas',
  },
  MRU: {
    code: 'MRU',
    symbol: 'UM',
    name: 'Mauritanian Ouguiya',
    symbolNative: 'أ.م',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Mauritanian ouguiyas',
  },
  MUR: {
    code: 'MUR',
    symbol: '₨',
    name: 'Mauritian Rupee',
    symbolNative: '₨',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Mauritian rupees',
  },
  MVR: {
    code: 'MVR',
    symbol: 'Rf',
    name: 'Maldivian Rufiyaa',
    symbolNative: 'ރ.',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Maldivian rufiyaas',
  },
  MWK: {
    code: 'MWK',
    symbol: 'MK',
    name: 'Malawian Kwacha',
    symbolNative: 'MK',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Malawian kwachas',
  },
  MXN: {
    code: 'MXN',
    symbol: '$',
    name: 'Mexican Peso',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Mexican pesos',
  },
  MYR: {
    code: 'MYR',
    symbol: 'RM',
    name: 'Malaysian Ringgit',
    symbolNative: 'RM',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Malaysian ringgits',
  },
  MZN: {
    code: 'MZN',
    symbol: 'MT',
    name: 'Mozambican Metical',
    symbolNative: 'MT',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Mozambican meticals',
  },
  NAD: {
    code: 'NAD',
    symbol: 'N$',
    name: 'Namibian Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Namibian dollars',
  },
  NGN: {
    code: 'NGN',
    symbol: '₦',
    name: 'Nigerian Naira',
    symbolNative: '₦',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Nigerian nairas',
  },
  NIO: {
    code: 'NIO',
    symbol: 'C$',
    name: 'Nicaraguan Córdoba',
    symbolNative: 'C$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Nicaraguan córdobas',
  },
  NOK: {
    code: 'NOK',
    symbol: 'kr',
    name: 'Norwegian Krone',
    symbolNative: 'kr',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Norwegian kroner',
  },
  NPR: {
    code: 'NPR',
    symbol: '₨',
    name: 'Nepalese Rupee',
    symbolNative: 'रु',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Nepalese rupees',
  },
  NZD: {
    code: 'NZD',
    symbol: 'NZ$',
    name: 'New Zealand Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'New Zealand dollars',
  },
  OMR: {
    code: 'OMR',
    symbol: '﷼',
    name: 'Omani Rial',
    symbolNative: 'ر.ع.',
    decimalDigits: 3,
    rounding: 0.001,
    namePlural: 'Omani rials',
  },
  PAB: {
    code: 'PAB',
    symbol: 'B/.',
    name: 'Panamanian Balboa',
    symbolNative: 'B/.',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Panamanian balboas',
  },
  PEN: {
    code: 'PEN',
    symbol: 'S/',
    name: 'Peruvian Sol',
    symbolNative: 'S/',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Peruvian soles',
  },
  PGK: {
    code: 'PGK',
    symbol: 'K',
    name: 'Papua New Guinean Kina',
    symbolNative: 'K',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Papua New Guinean kina',
  },
  PHP: {
    code: 'PHP',
    symbol: '₱',
    name: 'Philippine Peso',
    symbolNative: '₱',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Philippine pesos',
  },
  PKR: {
    code: 'PKR',
    symbol: '₨',
    name: 'Pakistani Rupee',
    symbolNative: '₨',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Pakistani rupees',
  },
  PLN: {
    code: 'PLN',
    symbol: 'zł',
    name: 'Polish Zloty',
    symbolNative: 'zł',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Polish zlotys',
  },
  PYG: {
    code: 'PYG',
    symbol: '₲',
    name: 'Paraguayan Guarani',
    symbolNative: '₲',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Paraguayan guaranis',
  },
  QAR: {
    code: 'QAR',
    symbol: 'QR',
    name: 'Qatari Riyal',
    symbolNative: 'ر.ق',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Qatari riyals',
  },
  RON: {
    code: 'RON',
    symbol: 'lei',
    name: 'Romanian Leu',
    symbolNative: 'lei',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Romanian lei',
  },
  RSD: {
    code: 'RSD',
    symbol: 'дин',
    name: 'Serbian Dinar',
    symbolNative: 'дин',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Serbian dinars',
  },
  RUB: {
    code: 'RUB',
    symbol: '₽',
    name: 'Russian Ruble',
    symbolNative: '₽',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Russian rubles',
  },
  RWF: {
    code: 'RWF',
    symbol: 'FRw',
    name: 'Rwandan Franc',
    symbolNative: 'FRw',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Rwandan francs',
  },
  SAR: {
    code: 'SAR',
    symbol: '﷼',
    name: 'Saudi Riyal',
    symbolNative: 'ر.س',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Saudi riyals',
  },
  SBD: {
    code: 'SBD',
    symbol: 'SI$',
    name: 'Solomon Islands Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Solomon Islands dollars',
  },
  SCR: {
    code: 'SCR',
    symbol: '₨',
    name: 'Seychellois Rupee',
    symbolNative: '₨',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Seychellois rupees',
  },
  SDG: {
    code: 'SDG',
    symbol: 'SDG',
    name: 'Sudanese Pound',
    symbolNative: 'ج.س.',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Sudanese pounds',
  },
  SEK: {
    code: 'SEK',
    symbol: 'kr',
    name: 'Swedish Krona',
    symbolNative: 'kr',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Swedish kronor',
  },
  SGD: {
    code: 'SGD',
    symbol: 'S$',
    name: 'Singapore Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Singapore dollars',
  },
  SHP: {
    code: 'SHP',
    symbol: '£',
    name: 'Saint Helena Pound',
    symbolNative: '£',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Saint Helena pounds',
  },
  SLL: {
    code: 'SLL',
    symbol: 'Le',
    name: 'Sierra Leonean Leone',
    symbolNative: 'Le',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Sierra Leonean leones',
  },
  SOS: {
    code: 'SOS',
    symbol: 'S',
    name: 'Somali Shilling',
    symbolNative: 'S',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Somali shillings',
  },
  SRD: {
    code: 'SRD',
    symbol: '$',
    name: 'Surinamese Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Surinamese dollars',
  },
  SSP: {
    code: 'SSP',
    symbol: '£',
    name: 'South Sudanese Pound',
    symbolNative: '£',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'South Sudanese pounds',
  },
  STN: {
    code: 'STN',
    symbol: 'Db',
    name: 'São Tomé and Príncipe Dobra',
    symbolNative: 'Db',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'São Tomé and Príncipe dobras',
  },
  SYP: {
    code: 'SYP',
    symbol: '£',
    name: 'Syrian Pound',
    symbolNative: 'ل.س',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Syrian pounds',
  },
  SZL: {
    code: 'SZL',
    symbol: 'E',
    name: 'Swazi Lilangeni',
    symbolNative: 'E',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Swazi emalangeni',
  },
  THB: {
    code: 'THB',
    symbol: '฿',
    name: 'Thai Baht',
    symbolNative: '฿',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Thai baht',
  },
  TJS: {
    code: 'TJS',
    symbol: 'SM',
    name: 'Tajikistani Somoni',
    symbolNative: 'SM',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Tajikistani somonis',
  },
  TMT: {
    code: 'TMT',
    symbol: 'T',
    name: 'Turkmenistani Manat',
    symbolNative: 'T',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Turkmenistani manats',
  },
  TND: {
    code: 'TND',
    symbol: 'DT',
    name: 'Tunisian Dinar',
    symbolNative: 'د.ت',
    decimalDigits: 3,
    rounding: 0.001,
    namePlural: 'Tunisian dinars',
  },
  TOP: {
    code: 'TOP',
    symbol: 'T$',
    name: 'Tongan Paʻanga',
    symbolNative: 'T$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Tongan paʻanga',
  },
  TRY: {
    code: 'TRY',
    symbol: '₺',
    name: 'Turkish Lira',
    symbolNative: '₺',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Turkish liras',
  },
  TTD: {
    code: 'TTD',
    symbol: 'TT$',
    name: 'Trinidad and Tobago Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Trinidad and Tobago dollars',
  },
  TWD: {
    code: 'TWD',
    symbol: 'NT$',
    name: 'New Taiwan Dollar',
    symbolNative: 'NT$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'New Taiwan dollars',
  },
  TZS: {
    code: 'TZS',
    symbol: 'TSh',
    name: 'Tanzanian Shilling',
    symbolNative: 'TSh',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Tanzanian shillings',
  },
  UAH: {
    code: 'UAH',
    symbol: '₴',
    name: 'Ukrainian Hryvnia',
    symbolNative: '₴',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Ukrainian hryvnias',
  },
  UGX: {
    code: 'UGX',
    symbol: 'USh',
    name: 'Ugandan Shilling',
    symbolNative: 'USh',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Ugandan shillings',
  },
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'US dollars',
  },
  UYU: {
    code: 'UYU',
    symbol: '$U',
    name: 'Uruguayan Peso',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Uruguayan pesos',
  },
  UZS: {
    code: 'UZS',
    symbol: 'сўм',
    name: 'Uzbekistani Som',
    symbolNative: 'сўм',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Uzbekistani soms',
  },
  VES: {
    code: 'VES',
    symbol: 'Bs',
    name: 'Venezuelan Bolívar',
    symbolNative: 'Bs',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Venezuelan bolívars',
  },
  VND: {
    code: 'VND',
    symbol: '₫',
    name: 'Vietnamese Dong',
    symbolNative: '₫',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Vietnamese dong',
  },
  VUV: {
    code: 'VUV',
    symbol: 'VT',
    name: 'Vanuatu Vatu',
    symbolNative: 'VT',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Vanuatu vatus',
  },
  WST: {
    code: 'WST',
    symbol: 'WS$',
    name: 'Samoan Tala',
    symbolNative: 'T',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Samoan tala',
  },
  XAF: {
    code: 'XAF',
    symbol: 'FCFA',
    name: 'Central African CFA Franc',
    symbolNative: 'FCFA',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'Central African CFA francs',
  },
  XCD: {
    code: 'XCD',
    symbol: 'EC$',
    name: 'East Caribbean Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'East Caribbean dollars',
  },
  XOF: {
    code: 'XOF',
    symbol: 'CFA',
    name: 'West African CFA Franc',
    symbolNative: 'CFA',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'West African CFA francs',
  },
  XPF: {
    code: 'XPF',
    symbol: 'CFP',
    name: 'CFP Franc',
    symbolNative: 'CFP',
    decimalDigits: 0,
    rounding: 1,
    namePlural: 'CFP francs',
  },
  YER: {
    code: 'YER',
    symbol: '﷼',
    name: 'Yemeni Rial',
    symbolNative: 'ر.ي',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Yemeni rials',
  },
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    name: 'South African Rand',
    symbolNative: 'R',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'South African rand',
  },
  ZMW: {
    code: 'ZMW',
    symbol: 'ZK',
    name: 'Zambian Kwacha',
    symbolNative: 'ZK',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Zambian kwachas',
  },
  ZWL: {
    code: 'ZWL',
    symbol: 'Z$',
    name: 'Zimbabwean Dollar',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0.01,
    namePlural: 'Zimbabwean dollars',
  },
};

export function getCurrencySymbol(
  currency: string,
  native: boolean = false
): string {
  const info = CURRENCIES[currency];
  if (!info) return currency;
  return native ? info.symbolNative : info.symbol;
}

/**
 * Ceiling for a stored or manually entered exchange rate. The widest real
 * pair (IRR per KWD) sits near 1.4e5, so 1e6 leaves headroom while still
 * catching an extra typed digit or a converted total pasted into the rate
 * field. The API schemas enforce it; both clients check it before setting
 * an override.
 */
export const EXCHANGE_RATE_MAX = 10_000_000;
